import { useKeycloak } from "@react-keycloak/web";
import { Worker } from "@react-pdf-viewer/core";
import { App as AntApp, ConfigProvider } from "antd";
import { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./app/store";
import PrivateRoute from "./components/PrivateRoute";
import CustomEmpty from "./components/ui/CustomEmpty";
import Loader from "./components/ui/Loader";
import { antdThemeConfig } from "./constants/config";
import { pdfWorkerURL } from "./constants/constants";
import { SocketProvider } from "./contexts/SocketContext";
import { useLoginWithTokenMutation } from "./features/auth/authApi";
import { userLoggedIn, userLoggedOut } from "./features/auth/authSlice";
import useAuthChecked from "./hooks/useAuthCheck";
import useError from "./hooks/useError";
import useFirebaseNotification from "./hooks/useFirebaseNotification";
import { publicRouters, sidebarRouters } from "./routers/routers";
import { removeHash } from "./utils/hash";

export default function App() {
  const authLoading = useAuthChecked();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const { contextHolder } = useFirebaseNotification();
  const { keycloak, initialized } = useKeycloak();
  const [localLoading, setLocalLoading] = useState(true);
  const [isLoad, setIsLoad] = useState(false);
  const { notificationToken, role } = useAppSelector((state) => state.auth);
  const [search] = useSearchParams();

  const [
    loginWithToken,
    {
      data: userData,
      isLoading: userLoading,
      isError: isEmployeeDetailsError,
      error: employeeDetailsError,
    },
  ] = useLoginWithTokenMutation();
  useError(
    isEmployeeDetailsError,
    employeeDetailsError,
    "Get employee details fail",
  );

  useEffect(() => {
    const type = search.get("type");
    const dynamicId = search.get("dynamic-id");
    const dynamicKeyword = search.get("dynamic-keyword");

    if (type) {
      localStorage.setItem("product-type", JSON.stringify(type));
    }

    if (dynamicId && dynamicKeyword) {
      localStorage.setItem(
        "dynamic",
        JSON.stringify({
          dynamicId,
          dynamicKeyword,
        }),
      );
    }

    if (initialized && keycloak?.token) {
      dispatch(
        userLoggedIn({
          access_token: keycloak?.token,
        }),
      );

      const productType = localStorage.getItem("product-type");
      const productId =
        (productType ? (removeHash(JSON.parse(productType)) as string) : 14) ||
        14;

      loginWithToken({
        mobile_token: notificationToken,
        keycloak_token: keycloak?.token,
        productId: Number(productId),
      });
    } else {
      dispatch(userLoggedOut());
    }

    setIsLoad(true);

    const t = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(t);
  }, [
    dispatch,
    initialized,
    keycloak?.token,
    loginWithToken,
    notificationToken,
    search,
  ]);

  // useEffect(() => {
  //   if (initialized && keycloak?.token) {
  //     dispatch(
  //       userLoggedIn({
  //         access_token: keycloak?.token,
  //       })
  //     );

  //     const productType = localStorage.getItem("product-type");
  //     const productId = productType
  //       ? (removeHash(JSON.parse(productType)) as string)
  //       : 14;

  //     loginWithToken({
  //       mobile_token: notificationToken,
  //       keycloak_token: keycloak?.token,
  //       productId: Number(productId),
  //     });
  //   } else {
  //     dispatch(userLoggedOut());
  //   }

  //   setIsLoad(true);
  // }, [
  //   dispatch,
  //   initialized,
  //   keycloak?.token,
  //   loginWithToken,
  //   notificationToken,
  // ]);

  useEffect(() => {
    if (isLoad) {
      if (!userData?.data?.token) {
        setLocalLoading(false);
        return;
      }

      const loggedData = {
        access_token: userData?.data?.token,
        user: userData?.data,
        role: "SUPER_ADMIN",
      };

      dispatch(userLoggedIn(loggedData));
    }

    const t = setTimeout(() => setLocalLoading(false), 500);
    return () => clearTimeout(t);
  }, [dispatch, isLoad, userData?.data]);

  // console.log({ initialized, userLoading, authLoading, localLoading });

  const isAppLoading =
    !initialized || userLoading || authLoading || localLoading || loading;

  return (
    <Worker workerUrl={pdfWorkerURL}>
      <SocketProvider>
        <ConfigProvider
          theme={antdThemeConfig}
          renderEmpty={() => <CustomEmpty rootClassName="py-20" />}
        >
          <AntApp>
            {contextHolder}

            {isAppLoading ? (
              <Loader />
            ) : (
              <Routes>
                <Route
                  path="/"
                  element={
                    <Navigate
                      to={
                        role === "super_admin" || role === "Admin"
                          ? "/admin/dashboard"
                          : "/user/dashboard"
                      }
                    />
                  }
                />
                <Route
                  path="/admin"
                  element={<Navigate to="/admin/dashboard" />}
                />
                <Route
                  path="/user"
                  element={<Navigate to="/user/dashboard" />}
                />
                <Route
                  path="/dashboard"
                  element={
                    <Navigate
                      to={
                        role === "super_admin" || role === "Admin"
                          ? "/admin/dashboard"
                          : "/user/dashboard"
                      }
                    />
                  }
                />
                <Route
                  path="/admin/communication/email"
                  element={<Navigate to="/admin/communication/email/inbox" />}
                />

                <Route
                  path="/user/communication/email"
                  element={<Navigate to="/user/communication/email/inbox" />}
                />

                {/* Public routers */}
                {publicRouters.map(({ path, element: Element }, idx) => (
                  <Route
                    path={path}
                    key={idx + path}
                    element={
                      <Suspense fallback={<Loader />}>
                        <Element />
                      </Suspense>
                    }
                  />
                ))}

                {/* sidebar routers */}
                {sidebarRouters.map(
                  ({ path, element: Element, roles }, idx) => (
                    <Route
                      path={path}
                      key={path + idx}
                      element={
                        <PrivateRoute roles={roles}>
                          <Suspense fallback={<Loader />}>
                            <Element />
                          </Suspense>
                        </PrivateRoute>
                      }
                    />
                  ),
                )}
              </Routes>
            )}
          </AntApp>
        </ConfigProvider>
      </SocketProvider>
    </Worker>
  );
}
